import { Component, inject, signal, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntil, Subject } from 'rxjs';
import { ConnectionService } from '../../shared/services/connection/connection.service';
import {
  BoxWebhookService,
  BoxWebhookReadiness,
  BoxWebhook,
} from '../../shared/services/box-webhook/box-webhook.service';
import { MessageService } from '../../shared/services/message/message.service';
import { Connection, ConnectionType } from '../../shared/interfaces';

export interface BoxWebhookSetupDialogData {
  projectId: number;
  routineId: number;
}

/** Box event triggers offered for a webhook (the common file/folder events). */
const BOX_TRIGGERS = [
  'FILE.UPLOADED',
  'FILE.DELETED',
  'FILE.TRASHED',
  'FILE.RESTORED',
  'FILE.COPIED',
  'FILE.MOVED',
  'FILE.RENAMED',
  'FILE.VERSION.UPLOADED',
  'FOLDER.CREATED',
  'FOLDER.DELETED',
  'FOLDER.MOVED',
  'FOLDER.RENAMED',
];

@Component({
  selector: 'app-box-webhook-setup-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './box-webhook-setup-dialog.component.html',
  styleUrls: ['./box-webhook-setup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoxWebhookSetupDialogComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly connectionService = inject(ConnectionService);
  private readonly webhookService = inject(BoxWebhookService);
  private readonly message = inject(MessageService);
  private readonly dialogRef = inject(MatDialogRef<BoxWebhookSetupDialogComponent>);
  readonly data = inject<BoxWebhookSetupDialogData>(MAT_DIALOG_DATA);
  private readonly destroy$ = new Subject<void>();

  readonly triggers = BOX_TRIGGERS;
  readonly boxConnections = signal<Connection[]>([]);
  readonly readiness = signal<BoxWebhookReadiness | null>(null);
  readonly existingWebhooks = signal<BoxWebhook[]>([]);
  readonly loadingReadiness = signal(false);
  readonly provisioning = signal(false);

  readonly form = this.fb.group({
    connectionId: ['' as number | '', [Validators.required]],
    folderId: ['', [Validators.required]],
    triggers: [[] as string[], [Validators.required]],
  });

  ngOnInit(): void {
    this.connectionService
      .getConnectionsByType(this.data.projectId, ConnectionType.Box)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (connections) => this.boxConnections.set(connections),
        error: () => this.message.showMessage('error', 'Failed to load Box connections'),
      });

    this.form
      .get('connectionId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        if (typeof id === 'number') {
          this.refreshReadiness(id);
          this.refreshWebhooks(id);
        } else {
          this.readiness.set(null);
          this.existingWebhooks.set([]);
        }
      });
  }

  private refreshReadiness(connectionId: number): void {
    this.loadingReadiness.set(true);
    this.webhookService
      .getReadiness(this.data.projectId, connectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          this.readiness.set(r);
          this.loadingReadiness.set(false);
        },
        error: () => {
          this.readiness.set(null);
          this.loadingReadiness.set(false);
        },
      });
  }

  private refreshWebhooks(connectionId: number): void {
    this.webhookService
      .list(this.data.projectId, connectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.existingWebhooks.set(res.entries ?? []),
        error: () => this.existingWebhooks.set([]),
      });
  }

  canProvision(): boolean {
    return this.form.valid && !!this.readiness()?.ready && !this.provisioning();
  }

  provision(): void {
    if (!this.canProvision()) {
      return;
    }
    const value = this.form.getRawValue();
    this.provisioning.set(true);
    this.webhookService
      .create(this.data.projectId, {
        connectionId: value.connectionId as number,
        routineId: this.data.routineId,
        folderId: value.folderId as string,
        triggers: value.triggers as string[],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.provisioning.set(false);
          this.message.showMessage('success', 'Box webhook provisioned');
          this.refreshWebhooks(value.connectionId as number);
        },
        error: (err) => {
          this.provisioning.set(false);
          this.message.showMessage('error', err?.error?.message ?? 'Failed to provision Box webhook');
        },
      });
  }

  deleteWebhook(webhook: BoxWebhook): void {
    const connectionId = this.form.get('connectionId')?.value;
    if (typeof connectionId !== 'number') {
      return;
    }
    this.webhookService
      .delete(this.data.projectId, webhook.id, connectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.message.showMessage('success', 'Box webhook deleted');
          this.refreshWebhooks(connectionId);
        },
        error: (err) =>
          this.message.showMessage('error', err?.error?.message ?? 'Failed to delete Box webhook'),
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    // Always tear down subscriptions, including when the dialog is dismissed via
    // ESC/backdrop (which never calls close()).
    this.destroy$.next();
    this.destroy$.complete();
  }
}
